import React from 'react';
import { ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { withLang } from './LanguageRouter';
import EditableText from './EditableText';

const Footer = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#050C1A] text-white relative overflow-hidden border-t border-slate-800">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" 
             style={{
               backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
             }}>
        </div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="container mx-auto px-6 py-16 max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Contact Section */}
            <div>
              <EditableText
                section="footer"
                field="contactTitle"
                as="h3"
                className="text-xl font-bold text-blue-400 mb-6"
              >
                {t('nav.contact')}
              </EditableText>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <span className="text-blue-400 mr-2">📍</span>
                    <EditableText section="footer" field="cityName" as="span">Xiamen</EditableText>
                  </h4>
                  <p className="text-slate-400 text-sm">
                    {t('contact.emailLabel')}: <EditableText section="footer" field="email" as="span">office@towertech-xm.com</EditableText>
                  </p>
                  <p className="text-slate-400 text-sm">
                    {t('contact.locationLabel')}: <EditableText section="footer" field="location" as="span">Fujian Province, China</EditableText>
                  </p>
                  <p className="text-slate-400 text-sm">
                    {t('contact.foundedLabel')}: <EditableText section="footer" field="founded" as="span">2019</EditableText>
                  </p>
                </div>
              </div>
              
              <div className="mt-8">
                <EditableText
                  section="footer"
                  field="description"
                  as="p"
                  className="text-slate-400 text-sm leading-relaxed"
                >
                  {t('footer.description')}
                </EditableText>
              </div>
            </div>

            {/* Quick Links Section */}
            <div>
              <EditableText
                section="footer"
                field="quickLinksTitle"
                as="h3"
                className="text-xl font-bold text-blue-400 mb-6"
              >
                {t('footer.quickLinks')}
              </EditableText>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm"
                  >
                    <EditableText section="footer" field="homeLink" as="span">
                      {t('footer.home')}
                    </EditableText>
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm"
                  >
                    <EditableText section="footer" field="aboutLink" as="span">
                      {t('footer.about')}
                    </EditableText>
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm"
                  >
                    <EditableText section="footer" field="servicesLink" as="span">
                      {t('footer.services')}
                    </EditableText>
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm"
                  >
                    <EditableText section="footer" field="projectsLink" as="span">
                      {t('footer.projects')}
                    </EditableText>
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => document.getElementById('blog')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm"
                  >
                    <EditableText section="footer" field="blogLink" as="span">
                      {t('footer.blog')}
                    </EditableText>
                  </button>
                </li>
                <li>
                  <Link
                    to={withLang(currentLanguage, '/partners')}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm"
                    data-testid="footer-partners-link"
                  >
                    <EditableText section="footer" field="partnersLink" as="span">
                      {t('nav.partners')}
                    </EditableText>
                  </Link>
                </li>
                <li>
                  <button 
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm"
                  >
                    <EditableText section="footer" field="contactLink" as="span">
                      {t('footer.contact')}
                    </EditableText>
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal Information Section */}
            <div>
              <EditableText
                section="footer"
                field="legalTitle"
                as="h3"
                className="text-xl font-bold text-blue-400 mb-6"
              >
                {t('footer.legal')}
              </EditableText>
              <ul className="space-y-3 mb-8">
                <li>
                  <Link to={withLang(currentLanguage, '/terms')} className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm">
                    <EditableText section="footer" field="termsLink" as="span">
                      {t('footer.termsOfService')}
                    </EditableText>
                  </Link>
                </li>
                <li>
                  <Link to={withLang(currentLanguage, '/privacy')} className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm">
                    <EditableText section="footer" field="privacyLink" as="span">
                      {t('footer.privacyPolicy')}
                    </EditableText>
                  </Link>
                </li>
                <li>
                  <Link to={withLang(currentLanguage, '/cookie-policy')} className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm">
                    <EditableText section="footer" field="cookieLink" as="span">
                      {t('footer.cookiePolicy')}
                    </EditableText>
                  </Link>
                </li>
                <li>
                  <Link to={withLang(currentLanguage, '/gdpr')} className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-sm">
                    <EditableText section="footer" field="gdprLink" as="span">
                      {t('footer.gdprCompliance')}
                    </EditableText>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Languages Section */}
            <div>
              <EditableText
                section="footer"
                field="languagesTitle"
                as="h3"
                className="text-xl font-bold text-blue-400 mb-6"
              >
                {t('footer.languagesWeSpeak')}
              </EditableText>
              <div className="mb-8">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-slate-400">🇺🇸 {t('languages.en')}</span>
                  <span className="text-slate-400">🇪🇸 {t('languages.es')}</span>
                  <span className="text-slate-400">🇩🇪 {t('languages.de')}</span>
                  <span className="text-slate-400">🇸🇮 {t('languages.sl')}</span>
                  <span className="text-slate-400">🇷🇸 {t('languages.sr')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-blue-700 border-opacity-30">
          <div className="container mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-center md:text-left mb-4 md:mb-0">
                <p className="text-slate-500 text-sm">
                  © 2026 Xiamen Tower Technology Co., Ltd. - {t('footer.rights')}
                </p>
              </div>
              
              {/* Scroll to Top Button */}
              <button
                onClick={scrollToTop}
                className="bg-blue-600 hover:bg-blue-700 p-3 rounded-full transition-all duration-300 transform hover:scale-110 shadow-lg"
                aria-label="Scroll to top"
              >
                <ArrowUp className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;